import { useState, useEffect } from "react";
import axios from "axios";
import Cookies from "js-cookie";
import { useLocation } from "react-router-dom";
import { useAlert } from '../../../constants/AlertProvider';
import { useNavigate } from "react-router";
import { ApiService } from "../../../api/services.gen";
import { getUserDetails } from "../../../utils/getUserDetails";
import { AddEducation } from "./AddEducation";
import { AddWorkExp } from "./AddWorkExp";
import { AddSkills } from "./AddSkills";
import { AddProjects } from "./AddProjects";
import { Country, State, City } from "country-state-city";
import ProfilePic from "../../../../assets/images/blank-profile-pic.png";
import ImageModal from "../../common/ImageModal";
import CircularProgress from '@mui/material/CircularProgress';
import Navbar from "../../../constants/Navbar"
import HomeNavbar from "../HomeNavbar";
import ResumeUploader from "../ResumeUploader";

export default function OnboardingProfile() {
    const location = useLocation();
    const isEditMode = location.state?.from === "edit";
    const [isLoading, setIsLoading] = useState(true);
    const [userData, setUserData] = useState({ profile_picture: "", address: "" });
    const [appData, setAppData] = useState({ summary: "", linkedin: "", portfolio: "", github: "", description: "" }); //description is been used as candidate title
    const [applicationId, setApplicationId] = useState(null);
    const [showImageModal, setShowImageModal] = useState(false);
    const [profilePic, setProfilePic] = useState("");
    const [showAddEducation, setShowAddEducation] = useState(false);
    const [showAddWorkExp, setShowAddWorkExp] = useState(false);
    const [showAddSkills, setShowAddSkills] = useState(false);
    const [showAddProjects, setShowAddProjects] = useState(false);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const showAlert = useAlert();

    // Location state
    const [city, setCity] = useState("");
    const [country, setCountry] = useState("");
    const [state, setState] = useState("");
    const [countries, setCountries] = useState([]);
    const [states, setStates] = useState([]);
    const [cities, setCities] = useState([]);
    const [selectedCountry, setSelectedCountry] = useState(null);
    const [selectedState, setSelectedState] = useState(null);
    const [latitude, setLatitude] = useState(null);
    const [longitude, setLongitude] = useState(null);

    useEffect(() => {
        const fetchProfile = async () => {
            setIsLoading(true);
            const userDetails = await getUserDetails();
            const userId = userDetails?.id;
            try {
                const userRes = await axios.get(`/api/users/${userId}/`, { withCredentials: true })
                setUserData(userRes.data);

                const allCountries = Country.getAllCountries();
                setCountries(allCountries);

                const address = userRes.data.address_details;
                const userCountryName = address?.country;
                const userStateName = address?.state_province;
                const userCityName = address?.city;
                setLatitude(address?.latitude);
                setLongitude(address?.longitude);

                if (userCountryName) {
                    const countryObj = allCountries.find(c => c.name === userCountryName);
                    if (countryObj) {
                        setSelectedCountry(countryObj);
                        setCountry(countryObj.name);
                        const countryStates = State.getStatesOfCountry(countryObj.isoCode);
                        setStates(countryStates);

                        if (userStateName) {
                            const stateObj = countryStates.find(s => s.name === userStateName);
                            if (stateObj) {
                                setSelectedState(stateObj);
                                setState(stateObj.name);
                                setCities(City.getCitiesOfState(countryObj.isoCode, stateObj.isoCode));
                            }
                        }
                    }
                }//tt
                setCity(userCityName || "");
                setProfilePic(userRes.data.profile_picture || "");

                const appId = userRes.data.applicationids[0].id;
                setApplicationId(appId);

                const appRes = await axios.get(`/api/application/${appId}/`, { withCredentials: true });
                setAppData(appRes.data);
            } catch (error) {
                showAlert("Error fetching user data", "error");
                console.error("Error fetching user data:", error);
            } finally {
                setIsLoading(false);
            }
        }
        fetchProfile();
    }, []);

    // Limits enforced by the Add* sub-forms, mirrored here so an import can't
    // create more rows than the user is allowed to keep.
    const MAX_EDUCATION = 2;
    const MAX_EXPERIENCE = 3;
    const MAX_SKILLS = 20;

    // The parser returns human dates ("Aug 2024", "2018", "Present"); the API
    // wants a date. Keep the year, which is all these forms collect anyway.
    const toApiDate = (value) => {
        const match = /(\d{4})/.exec(value || "");
        return match ? `${match[1]}-01-01` : null;
    };

    // GPA is stored as decimal(3,2), so only a plain number below 10 fits.
    // Resumes also use "7.5/10" and similar scales; keep the value, drop the
    // scale, and skip anything that won't round-trip.
    const toApiGpa = (value) => {
        const match = /(\d+(?:\.\d+)?)/.exec(value || "");
        if (!match) return null;
        const num = parseFloat(match[1]);
        return Number.isFinite(num) && num < 10 ? num.toFixed(2) : null;
    };

    /**
     * Apply parsed resume data to the profile: fill the basic text fields and
     * create Education / Experience / Skill records so the sections below are
     * actually populated. Returns a note describing what was imported so the
     * uploader can report it accurately.
     */
    const applyResumePrefill = async (parsed) => {
        const contact = parsed.contact_info || {};
        const firstTitle = parsed.experience?.[0]?.title || "";

        // Only fill empty fields so we never clobber user-typed input.
        setAppData((prev) => ({
            ...prev,
            summary: prev.summary || parsed.summary || "",
            description: prev.description || firstTitle,
            linkedin: prev.linkedin || contact.linkedin || "",
            portfolio: prev.portfolio || contact.portfolio || "",
            github: prev.github || contact.github || "",
        }));

        if (!applicationId) {
            return { note: "Autofilled your summary and links from your resume." };
        }

        const imported = { education: 0, experience: 0, skills: 0 };

        for (const edu of (parsed.education || []).slice(0, MAX_EDUCATION)) {
            if (!edu.institution && !edu.degree) continue;
            // start_date is required by the API; without a year we can't create
            // the row, so leave it for the user to add rather than guess.
            if (!toApiDate(edu.start_date)) continue;
            try {
                await ApiService.apiEducationCreate({
                    requestBody: {
                        institution: edu.institution || "",
                        degree: edu.degree || "",
                        field_of_study: edu.field_of_study || "",
                        gpa: toApiGpa(edu.gpa),
                        start_date: toApiDate(edu.start_date),
                        end_date: toApiDate(edu.end_date),
                        application: applicationId,
                    },
                });
                imported.education += 1;
            } catch (error) {
                console.error("Failed to import education from resume", error);
            }
        }

        for (const exp of (parsed.experience || []).slice(0, MAX_EXPERIENCE)) {
            if (!exp.company && !exp.title) continue;
            if (!toApiDate(exp.start_date)) continue;
            const isCurrent = /present|current|now/i.test(exp.end_date || "");
            try {
                await ApiService.apiExperienceCreate({
                    requestBody: {
                        company: exp.company || "",
                        title: exp.title || "",
                        city: exp.city || "",
                        state: exp.state || "",
                        country: exp.country || "",
                        is_remote: Boolean(exp.is_remote),
                        start_date: toApiDate(exp.start_date),
                        end_date: isCurrent ? null : toApiDate(exp.end_date),
                        responsibilities: exp.responsibilities || "",
                        application: applicationId,
                    },
                });
                imported.experience += 1;
            } catch (error) {
                console.error("Failed to import experience from resume", error);
            }
        }

        // Import only skills the parser could match to the standardized skill
        // catalog — unmatched strings are the noisiest part of extraction, and
        // importing them would leave the user deleting junk.
        const matchedSkills = (parsed.skills || [])
            .filter((skill) => skill.matching_skill && skill.name)
            .slice(0, MAX_SKILLS);
        for (const skill of matchedSkills) {
            try {
                await ApiService.apiSkillCreate({
                    requestBody: {
                        application: applicationId,
                        name: skill.name,
                        score: 5,
                        matching_skill_id: skill.matching_skill?.id || null,
                    },
                });
                imported.skills += 1;
            } catch (error) {
                console.error("Failed to import skill from resume", error);
            }
        }

        // Reveal the sections so the imported rows are visible for review.
        if (imported.education) setShowAddEducation(true);
        if (imported.experience) setShowAddWorkExp(true);
        if (imported.skills) setShowAddSkills(true);

        const parts = [];
        if (imported.experience) parts.push(`${imported.experience} experience`);
        if (imported.education) parts.push(`${imported.education} education`);
        if (imported.skills) parts.push(`${imported.skills} skills`);
        const detail = parts.length ? ` Imported ${parts.join(", ")}.` : "";
        return { note: `Autofilled your summary and links from your resume.${detail}` };
    };

    const handleImageCropped = async (file) => {
        try {
            const userDetails = await getUserDetails();
            const userId = userDetails?.id;
            const formData = new FormData();
            formData.append("profile_picture", file);

            // Get CSRF token from cookies
            const csrftoken = Cookies.get("csrftoken")

            const response = await axios.patch(
                `/api/users/${userId}/`,
                formData,
                {
                    headers: {
                        "Content-Type": "multipart/form-data",
                        ...(csrftoken && { "X-CSRFTOKEN": csrftoken }),
                    },
                    withCredentials: true,
                }
            );
            setProfilePic(response.data.profile_picture || URL.createObjectURL(file));
            setShowImageModal(false);
            showAlert("Profile updated!", "success");
        } catch (error) {
            setShowImageModal(false);
            showAlert("Failed to update profile", "error");
            console.error("error", error);
        }
    };

    const handleSave = async () => {
        setLoading(true);

        // --- Validation ---
        if (!appData.summary.trim()) {
            showAlert("Please provide a professional summary.", "error");
            setLoading(false);
            return;
        }
        if (!country || !state) {
            showAlert("Please select your full location (Country, State).", "error");
            setLoading(false);
            return;
        }

        const isValidUrl = (urlString) => {
            if (!urlString.trim()) return true; // Allow empty fields
            try {
                new URL(urlString);
                return true;
            } catch (_) {
                return false;
            }
        };

        if (!isValidUrl(appData.linkedin)) {
            showAlert("Please enter a valid LinkedIn URL", "error");
            setLoading(false);
            return;
        }

        try {
            const userDetails = await getUserDetails();
            const userId = userDetails?.id;
            if (!userId) {
                showAlert("User not found", "error");
                return;
            }

            // --- User and Address Data Handling ---
            const addressDetails = userData.address_details;
            const hasAddressChanged = city !== (addressDetails?.city || "") || state !== (addressDetails?.state_province || "") || country !== (addressDetails?.country || "");

            if (hasAddressChanged) {
                await ApiService.apiUsersPartialUpdate({
                    id: userId,
                    requestBody: {
                        address_data: { city, state_province: state, country, latitude, longitude }
                    }
                });
            }

            // --- Application Data Handling ---
            await ApiService.apiApplicationPartialUpdate({
                id: applicationId,
                requestBody: {
                    summary: appData.summary,
                    linkedin: appData.linkedin,
                    portfolio: appData.portfolio,
                    github: appData.github,
                    description: appData.description,
                }
            });

            showAlert("Profile updated!", "success");
            if (isEditMode) {
                navigate("/profile");
            } else {
                navigate("/setpreferences");
            }
        } catch (error) {
            showAlert("Failed to update profile", "error");
            console.error("error", error);
        } finally {
            setLoading(false);
        }
    };

    const handleCountryChange = (isoCode) => {
        const countryObj = countries.find((c) => c.isoCode === isoCode);
        setSelectedCountry(countryObj);
        setCountry(countryObj ? countryObj.name : "");
        setStates(State.getStatesOfCountry(isoCode));
        setSelectedState(null);
        setCities([]);
        setCity("");
    };

    const handleStateChange = (isoCode) => {
        const stateObj = states.find((s) => s.isoCode === isoCode);
        setSelectedState(stateObj);
        setState(stateObj ? stateObj.name : "");
        if (selectedCountry) {
            setCities(City.getCitiesOfState(selectedCountry.isoCode, isoCode));
        } else {
            setCities([]);
        }
        setCity("");
    };

    if (isLoading) {
        return (
            <>
                {isEditMode ? <HomeNavbar /> : <Navbar />}
                <div className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 text-center ${isEditMode && "mt-14"}`}>
                    <h1 className="text-2xl font-bold">Loading Your Profile...</h1>
                </div>
            </>
        );
    }

    return (
        <>
            {isEditMode ? <HomeNavbar /> : <Navbar />}
            <div className={`p-2 flex flex-col items-center ${isEditMode && "mt-14"}`}>
                <div className="flex flex-col p-4 md:p-6 w-full max-w-6xl gap-y-2">
                    <div className="w-full flex flex-col p-2 mb-4">
                        <h1 className="text-gray-900 dark:text-gray-100 font-bold text-lg md:text-3xl tracking-tight">{isEditMode ? "Update Your Info" : "A last few details, and we'll get your profile ready for matches!"}</h1>
                    </div>

                    <div className="flex flex-col lg:flex-row w-full gap-4">
                        <div className="w-full md:w-lg p-2 md:p-4">
                            <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">Profile</p>
                            <p className="text-gray-600 dark:text-gray-400 text-[14px]">This will be used to match you with the right opportunities</p>
                        </div>

                        <div className="flex flex-col flex-1 text-gray-900 dark:text-gray-100 p-2 md:p-4">
                            {/* <ResumeUploader /> */}
                            <ResumeUploader
                                isOnboarding={true}
                                onAutoPrefill={(parsed) => applyResumePrefill(parsed)}
                            />
                            <div className="flex flex-col gap-y-2 mb-8">
                                <label htmlFor="summary" className="text-[14px] font-medium">Summary*</label>
                                <textarea rows="2" cols="25" maxLength={500} value={appData.summary} onChange={e => setAppData({ ...appData, summary: e.target.value })} className="p-2 resize-none border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-[14px] focus:outline-none focus:border-blue-500 rounded-lg"></textarea>
                                <p className="text-gray-600 dark:text-gray-400 text-[14px] font-light">Describe your expertise and add profile summary in your own words</p>
                            </div>
                            <div className="flex flex-col gap-y-2 mb-8">
                                <label htmlFor="summary" className="text-[14px] font-medium">I'm a*</label>
                                <input value={appData.description} onChange={e => setAppData({ ...appData, description: e.target.value })} className="p-2 border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-[14px] focus:outline-none focus:border-blue-500 rounded-lg" placeholder="eg., Software Engineer"/>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                                <div className="md:col-span-2 flex items-end justify-between">
                                    <p className="text-[14px] font-medium">Address*</p>
                                </div>
                                <div>
                                    <label className="text-[14px] font-medium">Country*</label>
                                    <select
                                        value={selectedCountry?.isoCode || ""}
                                        onChange={(e) => handleCountryChange(e.target.value)}
                                        className="w-full p-2 text-sm border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:border-blue-500 rounded-lg"
                                        required
                                    >
                                        <option value="">Select Country</option>
                                        {countries.map((c) => (
                                            <option key={c.isoCode} value={c.isoCode}>
                                                {c.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="text-[14px] font-medium">State*</label>
                                    <select
                                        value={selectedState?.isoCode || ""}
                                        onChange={(e) => handleStateChange(e.target.value)}
                                        disabled={!selectedCountry}
                                        className="w-full p-2 text-sm border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:border-blue-500 rounded-lg disabled:bg-gray-100 dark:disabled:bg-gray-700"
                                    >
                                        <option value="">Select State</option>
                                        {states.map((s) => (
                                            <option key={s.isoCode} value={s.isoCode}>
                                                {s.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="md:col-span-2">
                                    <label className="text-[14px] font-medium">City*</label>
                                    <select
                                        value={city}
                                        onChange={(e) => setCity(e.target.value)}
                                        disabled={!selectedState}
                                        className="w-full p-2 text-sm border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:border-blue-500 rounded-lg disabled:bg-gray-100 dark:disabled:bg-gray-700"
                                        required
                                    >
                                        <option value="">Select City</option>
                                        {cities.map((c) => (
                                            <option key={c.name} value={c.name}>
                                                {c.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            {/* social links */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                                <div className="flex flex-col gap-y-2">
                                    <label htmlFor="linkedin" className="text-[14px] font-medium">Linkedin*</label>
                                    <input name="linkedin" id="linkedin" type="url" value={appData.linkedin} onChange={e => setAppData({ ...appData, linkedin: e.target.value })} className="max-w-md p-2 text-sm border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:border-blue-500 rounded-lg" placeholder="https://www.linkedin.com/in/..." />
                                </div>
                                <div className="flex flex-col gap-y-2">
                                    <label htmlFor="portfolio" className="text-[14px] font-medium">Portfolio</label>
                                    <input name="portfolio" id="portfolio" type="url" value={appData.portfolio} onChange={e => setAppData({ ...appData, portfolio: e.target.value })} className="max-w-md p-2 text-sm border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:border-blue-500 rounded-lg" placeholder="https://my-portfolio.com" />
                                </div>
                            </div>

                            <div className="flex flex-col gap-y-2 mb-8">
                                <label htmlFor="github" className="text-[14px] font-medium">Github</label>
                                <input name="github" id="github" type="url" value={appData.github} onChange={e => setAppData({ ...appData, github: e.target.value })} className="max-w-md p-2 text-sm border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:border-blue-500 rounded-lg" placeholder="https://github.com/your-username" />
                            </div>
                            {/* profile-photo */}
                            <div className="flex flex-col gap-y-2">
                                <p className="text-[14px] font-medium text-gray-900 dark:text-gray-100">Photo</p>
                                <div className="flex flex-row gap-x-4 items-center mb-8">
                                    <div className="w-12 h-12 rounded-full flex items-center justify-center shadow-md overflow-hidden">
                                        <img
                                            src={profilePic || ProfilePic}
                                            alt="Profile"
                                            className="w-full h-full object-cover"
                                        />
                                    </div>
                                    <button className="w-18 h-8 border-2 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-[14px] font-semibold"
                                        onClick={() => setShowImageModal(true)}
                                    >
                                        Change
                                    </button>
                                </div>
                                {showImageModal && (
                                    <ImageModal
                                        closeModal={() => setShowImageModal(false)}
                                        onImageCropped={handleImageCropped}
                                    />
                                )}
                            </div>

                            <div className="mt-2 flex flex-col w-full gap-y-4 mb-4">
                                {/* Education Section */}
                                <div className="flex flex-row justify-between">
                                    <h1 className="text-[24px] font-bold text-gray-900 dark:text-gray-100">Education</h1>
                                    <button
                                        type="button"
                                        className="text-[14px] font-semibold text-blue-700 cursor-pointer hover:text-blue-600 flex items-center gap-1"
                                        onClick={() => setShowAddEducation((prev) => !prev)}
                                    >
                                        <span className="text-[24px] font-light">{showAddEducation ? "-" : "+"}</span>
                                        <span>{showAddEducation ? "Collapse" : "Add Education"}</span>
                                    </button>
                                </div>
                                {showAddEducation && applicationId && (
                                    <AddEducation applicationId={applicationId} />
                                )}
                                <div className="flex flex-row justify-between">
                                    <h1 className="text-[24px] font-bold text-gray-900 dark:text-gray-100">Experience</h1>
                                    <button type="button" onClick={() => setShowAddWorkExp((prev) => !prev)} className="text-[14px] font-semibold text-blue-700 cursor-pointer hover:text-blue-600 flex items-center gap-1">
                                        <span className="text-[24px] font-light">{showAddWorkExp ? "-" : "+"}</span>
                                        <span>{showAddWorkExp ? "Collapse" : "Add Work Experience"}</span>
                                    </button>
                                </div>
                                {showAddWorkExp && applicationId && (
                                    <AddWorkExp applicationId={applicationId} />
                                )}
                                <div className="flex flex-row justify-between">
                                    <h1 className="text-[24px] font-bold text-gray-900 dark:text-gray-100">Skills</h1>
                                    <button type="button" onClick={() => setShowAddSkills((prev) => !prev)} className="text-[14px] font-semibold text-blue-700 cursor-pointer hover:text-blue-600 flex items-center gap-1">
                                        <span className="text-[24px] font-light">{showAddSkills ? "-" : "+"}</span>
                                        <span>{showAddSkills ? "Collapse" : "Add Skills"}</span>
                                    </button>
                                </div>
                                {showAddSkills && applicationId && (
                                    <AddSkills applicationId={applicationId} />
                                )}

                                <div className="flex flex-row justify-between">
                                    <h1 className="text-[24px] font-bold text-gray-900 dark:text-gray-100">Projects</h1>
                                    <button type="button" onClick={() => setShowAddProjects((prev) => !prev)} className="text-[14px] font-semibold text-blue-700 cursor-pointer hover:text-blue-600 flex items-center gap-1">
                                        <span className="text-[24px] font-light">{showAddProjects ? "-" : "+"}</span>
                                        <span>{showAddProjects ? "Collapse" : "Add Projects"}</span>
                                    </button>
                                </div>
                                {showAddProjects && applicationId && (
                                    <AddProjects applicationId={applicationId} />
                                )}
                            </div>
                        </div>
                    </div>
                    <hr className="w-full text-gray-300 dark:text-gray-700" />
                </div>
                <div className="w-full flex justify-end p-6">
                    <button
                        type="button"
                        onClick={handleSave}
                        disabled={loading} // optional: disable button while saving
                        className="text-white min-w-[180px] h-10 text-center font-medium text-sm bg-blue-700 px-4 rounded-lg cursor-pointer hover:bg-blue-600 flex items-center justify-center gap-2"
                    >
                        {loading ? (
                            <>
                                <CircularProgress size={20} color="inherit" />
                                Saving...
                            </>
                        ) : (
                            <>{isEditMode ? "Update Profile" : "Next, Add Preferences"}</>
                        )}
                    </button>
                </div>

            </div>
        </>
    );
}